/**
 * The BJCP style picker.
 *
 * A searchable popover rather than a `<select>` because there are 100+ styles
 * and the useful query is "ipa", "21", "saison" — which is exactly what
 * `searchCatalog` indexes (name, category name, and the `21A` code).
 *
 * Each row shows the code and the published vitals band, because the band is
 * the reason a brewer is choosing a style here at all: it is what the vitals
 * rail will be judged against a moment later.
 *
 * `AddStylePicker` is the same search for adding to a list of styles, which
 * the Additions tab uses.
 */

import type { Style } from '@beerwright/catalog';
import { getStyle, searchCatalog, styleCode } from '@beerwright/catalog';
import { CheckIcon, ChevronsUpDownIcon, PlusIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** `1.045–1.060 · 30–45 IBU · 5–10 SRM · 4.5–6.2 %` — whatever the style publishes. */
export function styleVitalsSummary(style: Style): string {
  const parts: string[] = [];
  const { og, ibu, srm, abv } = style.vitals;
  if (og !== null) parts.push(`${og.min.toFixed(3)}–${og.max.toFixed(3)}`);
  if (ibu !== null) parts.push(`${ibu.min.toFixed(0)}–${ibu.max.toFixed(0)} IBU`);
  if (srm !== null) parts.push(`${srm.min.toFixed(0)}–${srm.max.toFixed(0)} SRM`);
  if (abv !== null) parts.push(`${abv.min.toFixed(1)}–${abv.max.toFixed(1)} %`);
  return parts.length === 0 ? 'no published vitals' : parts.join(' · ');
}

export interface StylePickerProps {
  readonly styleId: string | null;
  readonly onChange: (styleId: string | null) => void;
}

const SEARCH_LIMIT = 60;

export function StylePicker({ styleId, onChange }: StylePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = styleId === null ? undefined : getStyle(styleId);
  const hits = useStyleSearch(query);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Style"
          className="h-9 w-full min-w-0 justify-between font-normal sm:h-8 sm:w-56"
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {selected === undefined ? (
              <span className="text-muted-foreground">Pick a style…</span>
            ) : (
              <>
                <span className="font-mono text-xs text-muted-foreground">
                  {styleCode(selected)}
                </span>{' '}
                {selected.name}
              </>
            )}
          </span>
          <ChevronsUpDownIcon className="opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* Capped at the room Radix leaves once the keyboard is padded out of
          the way, and the list is the part that gives. */}
      <PopoverContent
        align="start"
        className="flex max-h-(--radix-popover-content-available-height) w-[min(26rem,calc(100vw-2rem))] flex-col p-0"
      >
        <Command shouldFilter={false} className="flex min-h-0 flex-col">
          <CommandInput
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder="Search BJCP 2021 styles…"
          />
          <CommandList className="max-h-80 min-h-0 flex-1">
            <CommandEmpty>No style matches that.</CommandEmpty>
            <CommandGroup>
              {styleId !== null && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  Clear style
                </CommandItem>
              )}
              {hits.map((hit) => {
                const style = hit.entry as Style;
                return (
                  <StyleItem
                    key={style.id}
                    style={style}
                    checked={style.id === styleId}
                    onSelect={() => {
                      onChange(style.id);
                      setOpen(false);
                    }}
                  />
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** The styles matching a query, as the picker lists them. */
function useStyleSearch(query: string) {
  return useMemo(() => searchCatalog(query, { kinds: ['style'], limit: SEARCH_LIMIT }), [query]);
}

/** One style in the list: its code, name and published vitals band. */
function StyleItem({
  style,
  checked,
  onSelect,
}: {
  readonly style: Style;
  readonly checked: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <CommandItem value={style.id} onSelect={onSelect} className="items-start gap-2 py-2">
      <span className="mt-0.5 w-8 shrink-0 font-mono text-[11px] text-muted-foreground">
        {styleCode(style)}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium">{style.name}</span>
        <span className="truncate font-mono text-[11px] tabular-nums text-muted-foreground">
          {styleVitalsSummary(style)}
        </span>
      </span>
      <CheckIcon className={cn('mt-1 size-4 shrink-0', checked ? 'opacity-100' : 'opacity-0')} />
    </CommandItem>
  );
}

export interface AddStylePickerProps {
  /** Styles already chosen. They show a check, and choosing one again does nothing. */
  readonly styleIds: readonly string[];
  readonly onAdd: (styleId: string) => void;
}

/**
 * The same search as {@link StylePicker}, behind a fixed "Add style" button,
 * for a list of styles rather than one. Used by the Additions tab to tie a
 * profile to more styles.
 */
export function AddStylePicker({ styleIds, onAdd }: AddStylePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const hits = useStyleSearch(query);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <PlusIcon />
          Add style
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex max-h-(--radix-popover-content-available-height) w-[min(26rem,calc(100vw-2rem))] flex-col p-0"
      >
        <Command shouldFilter={false} className="flex min-h-0 flex-col">
          <CommandInput
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder="Search BJCP 2021 styles…"
          />
          <CommandList className="max-h-80 min-h-0 flex-1">
            <CommandEmpty>No style matches that.</CommandEmpty>
            <CommandGroup>
              {hits.map((hit) => {
                const style = hit.entry as Style;
                const added = styleIds.includes(style.id);
                return (
                  <StyleItem
                    key={style.id}
                    style={style}
                    checked={added}
                    onSelect={() => {
                      if (!added) onAdd(style.id);
                      setOpen(false);
                    }}
                  />
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
